import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaHome,
  FaChevronRight,
  FaCheck,
  FaCrown,
  FaGem,
  FaMedal,
  FaArrowRight,
  FaTimes,
  FaShieldAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { X, CreditCard } from "lucide-react";
import { useCookies } from "react-cookie";
import "../Vender/css/OurPackage.css";

const OurPackage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("silver"); // Default to popular
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [amount, setAmount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone_no, setPhone] = useState("");
  const sectionRef = useRef(null);
  const [cookies] = useCookies(["id"]);

  const packages = [
    {
      id: "silver",
      name: "Silver",
      tagline: "Perfect Start",
      icon: FaMedal,
      price: "6,49,999",
      duration: "15 Years",
      color: "#F59E0B", // Amber
      bgGradient: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
      features: [
        { text: "6N/7D Yearly", included: true },
        { text: "India + Asia", included: true },
        { text: "Only Accommodation", included: true },
        { text: "AMC ₹6999/year", included: true },
        { text: "3★ & 4★ Properties", included: true },
      ],
    },
    {
      id: "gold",
      name: "Gold",
      tagline: "Most Popular",
      icon: FaGem,
      price: "8,99,999",
      duration: "20 Years",
      color: "#6366F1", // Indigo
      bgGradient: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)",
      popular: true,
      features: [
        { text: "6N/7D Yearly", included: true },
        { text: "India + Asia", included: true },
        { text: "Only Accommodation", included: true },
        { text: "AMC ₹7999/year", included: true },
        { text: "3★ & 4★ Properties", included: true },
      ],
    },
    {
      id: "platinum",
      name: "Platinum",
      tagline: "Ultimate Luxury",
      icon: FaCrown,
      price: "11,99,999",
      duration: "25 Years",
      color: "#8B5CF6", // Purple
      bgGradient: "linear-gradient(135deg, #F3E8FF 0%, #EDE9FE 100%)",
      features: [
        { text: "6N/7D Yearly", included: true },
        { text: "India + International", included: true },
        { text: "Only Accommodation", included: true },
        { text: "AMC ₹9999/year", included: true },
        { text: "4★ & 5★ Properties", included: true },
      ],
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleGetStarted = (pkg) => {
    setSelectedPackage(pkg);
    setAmount(pkg.price.replace(/,/g, ""));
    setShowModal(true);
  };

  const handlePayment = async () => {
    if (!amount || !name || !email || !phone_no) {
      alert("Please fill in all fields.");
      return;
    }

    try {
      const res = await fetch("/api/payment_details/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: parseFloat(amount) }),
      });

      if (!res.ok) throw new Error(`API Error: ${res.status}`);

      const data = await res.json();

      const options = {
        key: process.env.RAZORPAY_KEY_ID,
        amount: data.amount * 100,
        currency: data.currency,
        name: "Aacore Resorts And Hospitality Pvt. Ltd.",
        description: `Payment for ${selectedPackage?.name} Package`,
        order_id: data.orderId,
        handler: async function (response) {
          const verifyRes = await fetch("/api/payment_details/verify-payment", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: parseFloat(amount),
              name,
              email,
              phone_no,
              user_id: cookies.id,
              package_type: selectedPackage?.name,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            alert("Payment successful 🎉");
            setShowModal(false);
          } else {
            alert("Payment failed ❌");
          }
        },
        prefill: { name, email, contact: phone_no },
        theme: { color: "#3399cc" },
      };

      const rzp1 = new window.Razorpay(options);
      rzp1.open();
    } catch (err) {
      console.error("Payment Error:", err);
      alert("Payment initialization failed.");
    }
  };

  return (
    <section
      className={`premium-packages-section ${isVisible ? "animate-in" : ""}`}
      ref={sectionRef}
    >
      <div className="pkg-container">
        {/* Breadcrumb Navigation */}
        <nav className="pkg-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="bc-item">
            <FaHome /> Home
          </Link>
          <FaChevronRight className="bc-sep" />
          <span className="bc-item active">Membership Plans</span>
        </nav>

        {/* Header Block */}
        <header className="pkg-header">
          <span className="pkg-subtitle">Choose Your Experience</span>
          <h2 className="pkg-title">Exclusive Hotel Memberships</h2>
          <p className="pkg-desc">
            Unlock a decade of luxury travel with our curated accommodation
            plans. Flexible, affordable, and designed for you.
          </p>
        </header>

        {/* Toggle Switch */}

        {/* CONTENT AREA */}
        <div className="pkg-content-area">
          {/* CARDS GRID */}
          <div className="pkg-grid">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              const isSelected = selectedPlan === pkg.id;

              return (
                <div
                  key={pkg.id}
                  className={`pkg-card ${pkg.popular ? "is-popular" : ""} ${
                    isSelected ? "is-selected" : ""
                  }`}
                  onClick={() => setSelectedPlan(pkg.id)}
                  style={{
                    "--theme-color": pkg.color,
                    "--anim-delay": `${index * 100}ms`,
                  }}
                >
                  {pkg.popular && (
                    <div className="ribbon">
                      <span>Best Value</span>
                    </div>
                  )}

                  <div className="card-top">
                    <div
                      className="icon-circle"
                      style={{ background: pkg.bgGradient, color: pkg.color }}
                    >
                      <Icon />
                    </div>
                    <div className="plan-meta">
                      <h3 className="plan-name">{pkg.name}</h3>
                      <span className="plan-tagline">{pkg.tagline}</span>
                    </div>
                  </div>

                  <div className="card-price-box">
                    <div className="price-row">
                      <span className="currency">₹</span>
                      <span className="amount">{pkg.price}</span>
                    </div>
                    <span className="validity">Valid for {pkg.duration}</span>
                  </div>

                  <ul className="card-features">
                    {pkg.features.map((feat, i) => (
                      <li key={i} className={!feat.included ? "excluded" : ""}>
                        <span className="check-icon">
                          {feat.included ? <FaCheck /> : <FaTimes />}
                        </span>
                        {feat.text}
                      </li>
                    ))}
                  </ul>

                  <div className="card-action">
                    <button
                      className="btn-select-plan"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGetStarted(pkg);
                      }}
                    >
                      Get Started <FaArrowRight />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Guarantee Footer */}
        <div className="pkg-guarantee">
          <FaShieldAlt className="guarantee-icon" />
          <div className="guarantee-text">
            <h4>100% Satisfaction Guaranteed</h4>
            <p>
              Detailed documentation and transparency in every transaction.{" "}
              <a href="#terms">View Terms</a>.
            </p>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            animation: "fadeIn 0.3s ease",
          }}
        >
          <div
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: "24px",
              width: "90%",
              maxWidth: "450px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              animation: "scaleIn 0.3s ease",
            }}
          >
            <div
              className="modal-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "24px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#1a202c",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  margin: 0,
                }}
              >
                <CreditCard size={24} style={{ color: "#667eea" }} />
                Pay for {selectedPackage?.name} Package
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#a0aec0",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: "24px" }}>
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                  }}
                >
                  Amount (₹)
                </label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="1"
                  max="10000000"
                  step="1"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                  }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                  }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#4a5568",
                    marginBottom: "8px",
                  }}
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter your phone"
                  value={phone_no}
                  onChange={(e) => setPhone(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                gap: "12px",
                padding: "24px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                className="btn-cancel"
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1,
                  padding: "14px 24px",
                  background: "#f7fafc",
                  color: "#4a5568",
                  border: "none",
                  borderRadius: "12px",
                  fontWeight: "600",
                  fontSize: "15px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                Cancel
              </button>
              <button
                className="btn-submit"
                onClick={handlePayment}
                style={{
                  flex: 1,
                  padding: "14px 24px",
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontWeight: "600",
                  fontSize: "15px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                }}
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default OurPackage;
